import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useCallback,
    useRef,
    Suspense,
    lazy,
} from 'react'
import { createRoot } from 'react-dom/client'
import RequestPasswordReset from 'components/auth/RequestPasswordReset'
import ResetPassword from 'components/auth/ResetPassword'
import AccountReviewPlaceholder from 'components/auth/AccountReviewPlaceholder'
import { idFromKey, debugPopover, debugCopy, memoizeDebounce } from 'utils'
import { accessTokens, setAccessToken, clearAccessTokens } from 'api/accessTokenMgmt'
import api from 'api/api'
import queryClient from 'api/queryClient'
import app from './application.css.scss' // needed to load global styles for the app
import {
    browserTracingIntegration,
    init as initializeSentry,
    isInitialized as isSentryInitialized,
} from '@sentry/react'
import { debounce } from 'lodash'
import {
    Route,
    useParams,
    useLocation,
    useNavigate,
    createBrowserRouter,
    createRoutesFromElements,
    Navigate,
    RouterProvider,
} from 'react-router-dom'
import axios from 'axios'
import { QueryClientProvider } from '@tanstack/react-query'
import queryString from 'query-string'
import { isMobile } from 'lib/device'
import LoadingScreen from 'components/shared/LoadingScreen'
import Signup from 'components/auth/Signup'
import Login from 'components/auth/Login'
import StateProvider from 'components/StateProvider'
import EditProfile from 'components/users/EditProfile'
import TeamSettings from 'components/teams/TeamSettings'
import StreamInvite from 'components/teams/StreamInvite'
import SpaceSettings from 'components/spaces/SpaceSettings'
import SpaceInvitation from 'components/spaces/SpaceInvitation'
import useStore, { resolveEffectiveTheme } from 'state/knovStore'
import KnovModal from 'components/shared/KnovModal'
import TrayHost from 'components/shared/TrayHost'
import { cacheQuest } from 'state/cache'
import { Subject } from 'rxjs'
import { map, filter } from 'rxjs/operators'
import { Message, MsgTypes, MsgSource, replyTo, sendAndAwaitReply } from './messaging/messaging'
import fileTypeChecker from 'file-type-checker'
import questModel from 'components/quests/questModel'
import {
    insertCenter,
    openCenter,
    openNotificationsStream,
    filterFromPath,
} from 'components/filters/useStreamFilters'
import { getCenterPanel, getSwiperRef } from 'state/imperativeApis/swiperApi'
import { useVersionCheck } from 'refactor/hooks/useVersionCheck'
// Make logging available to the app.
import { logEv } from 'lib/log'
import { initSentryOnce } from 'lib/sentryInit'
import { markStartupMetric } from 'lib/startupMetrics'
import { persistAppearanceThemePreference, THEME_STORAGE_KEY } from './constants/theme'
import { postMessageToNative } from 'utils/nativeBridge'
import { initNativeBackBridge } from 'utils/nativeBackBridge'
import { navigateBack } from 'state/appBackNavigation'
import { setDocumentTitleForPath } from 'utils/documentTitle'
import {
    getBootstrapLoadSource,
    getBootstrapSpaceId as resolveBootstrapSpaceId,
    normalizeBootstrapPayload,
    shouldReuseInlineBootstrapAuth,
} from './bootstrap/bootstrapPayload'
import { fetchBootstrapJson } from './bootstrap/bootstrapRequest'
import {
    ACCOUNT_REVIEW_PENDING,
    getAccountReviewAuthHint,
    getAuthSessionKey,
} from './bootstrap/accountReviewSession'
import { resolveMoneyColorMode } from 'lib/moneyColor'
import { radioStreamPath } from 'lib/treechatRadio'

// EventBus auto-initializes when useEventBus hook is used

const eventBus$ = new Subject()
const BOOTSTRAP_SCRIPT_ID = 'treechat-bootstrap-data'
const ACCOUNT_REVIEW_RECHECK_DELAY_MS = 5_000
const ACCOUNT_REVIEW_REQUEST_TIMEOUT_MS = 4_000
const ACCOUNT_REVIEW_BOOTSTRAP_SESSION_KEY = '__KNOV_ACCOUNT_REVIEW_BOOTSTRAP_SESSION_KEY__'
// Export eventBus$ for use in components that need to communicate with native app
export { eventBus$ as mobileEventBus$ }

function readInlineBootstrapPayload() {
    const bootstrapElem = document.getElementById(BOOTSTRAP_SCRIPT_ID)
    const bootstrapJson = bootstrapElem?.textContent?.trim()

    if (!bootstrapJson || bootstrapJson === 'null') {
        return null
    }

    try {
        return normalizeBootstrapPayload(JSON.parse(bootstrapJson), window.knovApiUrl)
    } catch (err) {
        console.error('Failed to parse inline bootstrap payload:', err)
        return null
    }
}

function parseAuthQuery(search) {
    const parsed = queryString.parse(search)
    return {
        space_invite: parsed.space_invite || '',
        stream_invite: parsed.stream_invite || '',
        stream_join: parsed.stream_join || '',
        wallet: parsed.wallet || '',
        oauth_signup: parsed.oauth_signup || window.history.state?.knovOauthSignup || '',
        prospect_token: parsed.prospect_token || '',
        utm_source: parsed.utm_source || '',
        utm_medium: parsed.utm_medium || '',
        utm_campaign: parsed.utm_campaign || '',
        utm_term: parsed.utm_term || '',
        utm_content: parsed.utm_content || '',
        gclid: parsed.gclid || '',
        gbraid: parsed.gbraid || '',
        wbraid: parsed.wbraid || '',
        landing_page: parsed.landing_page || '',
        landing_button: parsed.landing_button || '',
        landing_page_url: parsed.landing_page_url || '',
        landing_page_host: parsed.landing_page_host || '',
        landing_page_path: parsed.landing_page_path || '',
        landing_page_search: parsed.landing_page_search || '',
        landing_referrer: parsed.landing_referrer || '',
        amplitude_device_id: parsed.amplitude_device_id || '',
    }
}

function getBootstrapPayload() {
    if (window.__KNOV_BOOTSTRAP__ !== undefined) {
        return window.__KNOV_BOOTSTRAP__
    }

    window.__KNOV_BOOTSTRAP__ = readInlineBootstrapPayload()
    return window.__KNOV_BOOTSTRAP__
}

function cacheBootstrapPayload(bootstrapPayload) {
    const normalizedPayload = normalizeBootstrapPayload(bootstrapPayload, window.knovApiUrl)
    window.__KNOV_BOOTSTRAP__ = normalizedPayload
    return normalizedPayload
}

function getBootSpaceId(bootstrapPayload = getBootstrapPayload()) {
    return resolveBootstrapSpaceId(bootstrapPayload, window.Knov?.defaultSpace)
}

export async function getGon(cache = true, { redirectOnFailure = true, timeoutMs = 0 } = {}) {
    const inlineBootstrap = cache ? getBootstrapPayload() : null

    if (
        shouldReuseInlineBootstrapAuth({
            cache,
            inlineBootstrap,
        })
    ) {
        // Cacheable shells may carry public-safe inline bootstrap for fast paint,
        // but auth state must still come from a fresh request unless explicitly trusted.
        return inlineBootstrap
    }

    const tokens = accessTokens()
    const gon = await fetchBootstrapJson(`${window.knovApiUrl}/api/v1/gon`, {
        timeoutMs,
        headers: {
            ...tokens,
            Accept: 'application/json',
        },
    })
        .then(gon => cacheBootstrapPayload(gon))
        .catch(err => {
            console.error(err)
            if (redirectOnFailure) redirectToLoginScreen()
            return null // Return null to prevent undefined promise resolution
        })
    if (window.KNOVIGATOR_IS_MOBILE && tokens && Object.keys(tokens)?.length > 1) {
        sendAndAwaitReply(
            eventBus$,
            new Message(JSON.stringify({ tokens, gon }), MsgSource.Web, MsgTypes.Creds),
            { timeout: 500, retries: 10 },
        )
    }
    return gon
}

export function redirectToLoginScreen({ savePath = true } = { savePath: true }) {
    console.log('user is not logged in or token is expired, redirecting to login')
    const path = `${location.pathname}${location.search}`
    if (savePath && !path.startsWith('/login') && !path.startsWith('/signup')) {
        sessionStorage.setItem('attempted_path', path)
    }
    location.href = '/login'
}

export function redirectToSignupScreen() {
    location.href = '/signup'
}

async function fetchAgentModelConfig() {
    const agentModelConfig = await api.getAgentModelConfig()
    return agentModelConfig || useStore.getState().agentModelConfig
}

async function initApp() {
    markStartupMetric('init-app-started')
    const inlineBootstrap = getBootstrapPayload()
    const authSessionKey = currentAuthSessionKey()
    const accountReviewBootstrap =
        authSessionKey && window[ACCOUNT_REVIEW_BOOTSTRAP_SESSION_KEY] === authSessionKey
            ? window.gon
            : null
    window.gon = accountReviewBootstrap || (await getGon())
    const gon = window.gon
    const agentModelConfig = await fetchAgentModelConfig()
    useStore.getState().set({ agentModelConfig })

    // The pre-login bootstrap can only guess KNOVIGATOR_SPACE_ID (there is no currentUser yet),
    // and that guess sticks via the pending slot. Once the logged-in gon arrives, re-derive
    // instead: gon.space (server-side, membership-guarded) and the user's own space outrank the
    // raw env default — which can point at a space this deployment doesn't even have, 403-ing
    // every space-scoped request into a silently empty feed.
    let pendingSpaceId = window.__KNOV_PENDING_ACTIVE_SPACE_ID__
    if (pendingSpaceId && gon?.currentUser && pendingSpaceId === gon.KNOVIGATOR_SPACE_ID) {
        pendingSpaceId = null
    }
    const bootSpaceId = pendingSpaceId || getBootSpaceId(gon)
    if (bootSpaceId && useStore.getState().activeSpaceId !== bootSpaceId) {
        useStore.getState().set({ activeSpaceId: bootSpaceId })
    }

    markStartupMetric('bootstrap-payload-loaded', {
        hasCurrentUser: Boolean(gon?.currentUser),
        source: getBootstrapLoadSource(inlineBootstrap),
    })

    // Run non-critical initializations in parallel
    const initPromises = [
        // Sync active space to server
        gon.currentUser
            ? api.updateUser(gon.currentUser.id, { space_id: useStore.getState().activeSpaceId })
            : Promise.resolve(),
        // Initialize axios
        Promise.resolve(initAxios()),
        // Initialize service worker
        Promise.resolve(initServiceWorker()),
    ]

    // Only initialize analytics in production, and don't block on it
    if (gon.env === 'production') {
        try {
            setTimeout(() => {
                initAmplitude(window.gon?.currentUser?.id)
                initSentry()
            }, 0)
        } catch (err) {
            console.error('Failed to initialize analytics:', err)
        }
    }

    try {
        await Promise.all(initPromises)
    } catch (err) {
        console.error('Error during app initialization:', err)
    }

    markStartupMetric('init-app-complete', {
        hasCurrentUser: Boolean(gon?.currentUser),
    })

    return gon?.currentUser
}

const withAppLoader = WrappedComp => props => {
    const [loading, setLoading] = useState(true)
    const [finalProps, setFinalProps] = useState(props)
    const navigate = useNavigate()

    useEffect(function load() {
        ;(async function awaitLoad() {
            try {
                const currentUser = await initApp()

                // if load function provided use it, otherwise load StateProvider.
                if (props.load instanceof Function) {
                    let [loaded, loadedProps] = await props.load(props)
                    if (loaded) {
                        setFinalProps({ ...props, ...(loadedProps || {}) })
                        setLoading(false)
                    } else if (currentUser) navigate('/')
                    else redirectToLoginScreen()
                } else if (currentUser || props.load) {
                    setLoading(false)
                } else {
                    redirectToLoginScreen()
                }
            } catch (err) {
                console.error('Error in withAppLoader:', err)
                redirectToLoginScreen()
            }
        })()
    }, [])

    return (
        <div className="with-app-loader">
            {loading ? <LoadingScreen /> : <WrappedComp {...finalProps} />}
        </div>
    )
}

const withLayout = WrappedComp => props => {
    return (
        <>
            <div className="container-fluid" style={{ background: '#eee' }}>
                <div className="row">
                    <div className="col-md-offset-3 col-md-6">
                        <div className="row">
                            <WrappedComp {...props} />
                        </div>
                    </div>
                </div>
            </div>
            <KnovModal />
            <TrayHost />
        </>
    )
}

function UserSettingsLoader(props) {
    const UserSettingsWithLoader = withLayout(withAppLoader(EditProfile))
    return <UserSettingsWithLoader {...props} />
}

function StreamSettingsLoader(props) {
    const StreamSettingsWithLoader = withLayout(withAppLoader(TeamSettings))
    return <StreamSettingsWithLoader {...props} />
}

function SpaceSettingsLoader(props) {
    const SpaceSettingsWithLoader = withLayout(withAppLoader(SpaceSettings))
    return <SpaceSettingsWithLoader {...props} />
}

function SpaceInvitationLoader(props) {
    const SpaceInviteWithLoader = withLayout(withAppLoader(SpaceInvitation))
    return <SpaceInviteWithLoader {...props} />
}

function MetricsModalLoader() {
    const openMetricsModal = useStore(state => state.actions.modalActions.openMetricsModal)
    const isModalOpen = useStore(state => state.isModalOpen)
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if (!isModalOpen) {
            openMetricsModal()
            // Set loading to false after modal has been opened
            setTimeout(() => setIsLoading(false), 100)
        }
    }, [openMetricsModal, isModalOpen])

    // Listen for modal close and handle navigation
    useEffect(() => {
        if (!isModalOpen && !isLoading) {
            navigate('/')
        }
    }, [isModalOpen, isLoading, navigate])

    return isLoading ? (
        <div className="loading-metrics">
            <LoadingScreen />
        </div>
    ) : (
        <StateProviderLoader filter={{ all: true }} />
    )
}

function OrdinalsModalLoader() {
    const openOrdinalsModal = useStore(state => state.actions.modalActions.openOrdinalsModal)
    const isModalOpen = useStore(state => state.isModalOpen)
    const navigate = useNavigate()
    const { ordinalId } = useParams()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        // Only open the modal once on initial load; avoid reopening on close
        if (!isModalOpen && isLoading) {
            openOrdinalsModal(ordinalId || null)
            // Set loading to false after modal has been opened
            setTimeout(() => setIsLoading(false), 100)
        }
    }, [openOrdinalsModal, isModalOpen, ordinalId, isLoading])

    // Listen for modal close and handle navigation
    useEffect(() => {
        if (!isModalOpen && !isLoading) {
            navigate('/')
        }
    }, [isModalOpen, isLoading, navigate])

    return isLoading ? (
        <div className="loading-ordinals">
            <LoadingScreen />
        </div>
    ) : (
        <StateProviderLoader filter={{ all: true }} />
    )
}

function LeaderboardModalLoader() {
    const openLeaderboardModal = useStore(state => state.actions.modalActions.openLeaderboardModal)
    const isModalOpen = useStore(state => state.isModalOpen)
    const navigate = useNavigate()
    const { category } = useParams()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        // Only open the modal once on initial load; avoid reopening on close
        if (!isModalOpen && isLoading) {
            const allowed = new Set([
                'given',
                'received',
                'boosted',
                'posters',
                'ai_fees',
                'minters',
                'sharers',
            ])
            const initialCategory =
                category && allowed.has(category)
                    ? /** @type {'given' | 'received' | 'boosted' | 'posters' | 'ai_fees' | 'minters' | 'sharers'} */ (
                          category
                      )
                    : 'received'
            const reportDate = new URLSearchParams(window.location.search).get('date') || undefined
            openLeaderboardModal(initialCategory, reportDate)
            // Set loading to false after modal has been opened
            setTimeout(() => setIsLoading(false), 100)
        }
    }, [openLeaderboardModal, isModalOpen, category, isLoading])

    // Listen for modal close and handle navigation
    useEffect(() => {
        if (!isModalOpen && !isLoading) {
            navigate('/')
        }
    }, [isModalOpen, isLoading, navigate])

    return isLoading ? (
        <div className="loading-leaderboard">
            <LoadingScreen />
        </div>
    ) : (
        <StateProviderLoader filter={{ all: true }} />
    )
}

function TdpModalLoader() {
    const openTdpViewerModal = useStore(state => state.actions.modalActions.openTdpViewerModal)
    const isModalOpen = useStore(state => state.isModalOpen)
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        // Only open the modal once on initial load; avoid reopening on close
        if (!isModalOpen && isLoading) {
            openTdpViewerModal()
            setTimeout(() => setIsLoading(false), 100)
        }
    }, [openTdpViewerModal, isModalOpen, isLoading])

    useEffect(() => {
        if (!isModalOpen && !isLoading) {
            navigate('/')
        }
    }, [isModalOpen, isLoading, navigate])

    return isLoading ? (
        <div className="loading-tdp">
            <LoadingScreen />
        </div>
    ) : (
        <StateProviderLoader filter={{ all: true }} />
    )
}

// TDPViewer renders with three.js; keep it off the entry chunk.
const TDPViewer = lazy(() => import('components/modals/TDPViewer'))

function TdpSharePage() {
    const location = useLocation()
    const params = React.useMemo(() => new URLSearchParams(location.search), [location.search])
    const range = params.get('range')
    const theme = params.get('theme')

    const initialRange = /** @type {import('components/modals/TDPViewer').RangeKey} */ (
        range && ['today', '1d', '7d', '1mo', '1y', 'all'].includes(range) ? range : 'today'
    )
    const forcedTheme = theme === 'dark' ? 'dark' : 'light'

    // The share page renders outside StateProvider (which hosts the app's
    // QueryClientProvider), but TDPViewer's data hooks still need the client.
    return (
        <QueryClientProvider client={queryClient}>
            <Suspense fallback={null}>
                <TDPViewer initialRange={initialRange} forcedTheme={forcedTheme} />
            </Suspense>
        </QueryClientProvider>
    )
}

// Landing route for /p/<dailyEarningsShareId>. Post short links 302 on the
// server before the SPA boots, so the only /p/ paths that reach the router are
// minted daily-earnings shares: resolve the id and land on the sharer's
// profile (or home when the id is unknown).
function EarningsShareLandingPage() {
    const { shareId } = useParams()
    const navigate = useNavigate()

    useEffect(() => {
        let cancelled = false
        api.getDailyEarningsShare(shareId)
            .then(share => {
                if (cancelled) return
                const userId = share?.user?.id
                navigate(userId ? `/user/${userId}` : '/', { replace: true })
            })
            .catch(() => {
                if (!cancelled) navigate('/', { replace: true })
            })
        return () => {
            cancelled = true
        }
    }, [shareId])

    return (
        <div className="loading-earnings-share">
            <LoadingScreen />
        </div>
    )
}

function SettingsModalLoader() {
    const set = useStore(state => state.set)
    const isModalOpen = useStore(state => state.isModalOpen)
    const navigate = useNavigate()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if (!isModalOpen) {
            set(state => {
                state.isModalOpen = true
                state.modalParams = {
                    type: 'EditProfile',
                }
            })
            // Set loading to false after modal has been opened
            setTimeout(() => setIsLoading(false), 100)
        }

        // Check for Twitter OAuth callback parameters
        const searchParams = new URLSearchParams(location.search)
        const twitterConnected = searchParams.get('twitter_connected')
        const error = searchParams.get('error')

        if (twitterConnected === 'true') {
            // Show success message
            setTimeout(() => {
                set(state => {
                    state.isModalOpen = true
                    state.modalParams = {
                        type: 'Confirm',
                        title: 'Twitter Connected',
                        message: 'Your Twitter account has been successfully connected!',
                        confirmText: 'OK',
                        hideCancel: true,
                        onConfirm: () => {
                            set(state => {
                                state.isModalOpen = true
                                state.modalParams = {
                                    type: 'EditProfile',
                                }
                            })
                        },
                    }
                })
            }, 200)

            // Refresh user data to get updated provider info
            api.getGon().then(newGon => {
                if (newGon?.currentUser) {
                    window.gon = newGon
                }
            })
        } else if (error) {
            // Show error message
            setTimeout(() => {
                set(state => {
                    state.isModalOpen = true
                    state.modalParams = {
                        type: 'Confirm',
                        title: 'Twitter Connection Failed',
                        message: `Failed to connect Twitter account: ${error}`,
                        confirmText: 'OK',
                        hideCancel: true,
                        onConfirm: () => {
                            set(state => {
                                state.isModalOpen = true
                                state.modalParams = {
                                    type: 'EditProfile',
                                }
                            })
                        },
                    }
                })
            }, 200)
        }
    }, [])

    // Listen for modal close and handle navigation
    useEffect(() => {
        if (!isModalOpen && !isLoading) {
            navigate('/')
        }
    }, [isModalOpen, isLoading, navigate])

    return isLoading ? (
        <div className="loading-settings">
            <LoadingScreen />
        </div>
    ) : (
        <StateProviderLoader filter={{ all: true }} />
    )
}

function StateProviderLoader(props) {
    const StateProviderWithLoader = withAppLoader(StateProvider)
    return (
        <div className="state-provider-loader">
            <StateProviderWithLoader {...props} />
        </div>
    )
}

function LoginRedirector() {
    try {
        const tokenData = localStorage.getItem('token')
        if (tokenData) {
            const tokenStr = JSON.parse(tokenData)
            // TODO but what if it's the wrong token, for example if you switched users in the same browser ?
            if (tokenStr && tokenStr['access-token']) {
                return <StateProviderLoader />
            }
        }
    } catch (e) {
        console.log('Error retrieving accesstoken!', e)
    }
    return <Navigate to="/login" />
}

// /radio sits outside withAppLoader, so on a cold load (a guest following a
// link) gon is not fetched yet: load it here before picking the stream.
function RadioRedirect() {
    const [path, setPath] = useState(window.gon ? radioStreamPath(window.gon.RADIO_TEAM_ID) : null)

    useEffect(() => {
        if (path) return
        let cancelled = false
        getGon(true, { redirectOnFailure: false }).then(gon => {
            if (!cancelled) setPath(radioStreamPath(gon?.RADIO_TEAM_ID))
        })
        return () => {
            cancelled = true
        }
    }, [path])

    return path ? <Navigate to={path} replace /> : <LoadingScreen />
}

function currentAuthSessionKey() {
    return getAuthSessionKey(accessTokens())
}

function AccountReviewGate({ children }) {
    const location = useLocation()
    useLayoutEffect(() => {
        if (location.pathname !== '/signup') return
        const token = new URLSearchParams(location.search).get('oauth_signup')
        if (!token) return

        const cleanUrl = new URL(window.location.href)
        cleanUrl.searchParams.delete('oauth_signup')
        window.history.replaceState(
            { ...window.history.state, knovOauthSignup: token },
            '',
            `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`,
        )
    }, [location.pathname, location.search])
    const authSessionKey = currentAuthSessionKey()
    const authHint = getAccountReviewAuthHint(window, authSessionKey)
    const [reviewSession, setReviewSession] = useState(() => ({
        authSessionKey,
        state:
            authHint?.state === ACCOUNT_REVIEW_PENDING
                ? ACCOUNT_REVIEW_PENDING
                : authSessionKey
                ? 'checking'
                : 'approved',
    }))

    useEffect(() => {
        let cancelled = false
        let refreshInFlight = false
        let refreshTimeout = null

        if (!authSessionKey) {
            setReviewSession({ authSessionKey: null, state: 'approved' })
            return undefined
        }

        setReviewSession(current =>
            current.authSessionKey === authSessionKey
                ? current
                : {
                      authSessionKey,
                      state:
                          getAccountReviewAuthHint(window, authSessionKey)?.state ===
                          ACCOUNT_REVIEW_PENDING
                              ? ACCOUNT_REVIEW_PENDING
                              : 'checking',
                  },
        )

        const scheduleRefresh = () => {
            if (cancelled || refreshTimeout !== null) return

            refreshTimeout = window.setTimeout(() => {
                refreshTimeout = null
                void refreshReviewState()
            }, ACCOUNT_REVIEW_RECHECK_DELAY_MS)
        }

        const refreshReviewState = async () => {
            if (refreshInFlight) return
            refreshInFlight = true
            const gon = await getGon(false, {
                redirectOnFailure: false,
                timeoutMs: ACCOUNT_REVIEW_REQUEST_TIMEOUT_MS,
            })
            refreshInFlight = false
            if (cancelled) return
            if (!gon) {
                scheduleRefresh()
                return
            }

            window.gon = gon
            window[ACCOUNT_REVIEW_BOOTSTRAP_SESSION_KEY] = authSessionKey
            const nextReviewState =
                gon?.currentUser?.signup_review_status === ACCOUNT_REVIEW_PENDING
                    ? ACCOUNT_REVIEW_PENDING
                    : 'approved'
            if (nextReviewState === 'approved' && refreshTimeout !== null) {
                window.clearTimeout(refreshTimeout)
                refreshTimeout = null
            }
            setReviewSession({
                authSessionKey,
                state: nextReviewState,
            })
        }

        void refreshReviewState()
        scheduleRefresh()
        window.addEventListener('focus', refreshReviewState)

        return () => {
            cancelled = true
            if (refreshTimeout !== null) window.clearTimeout(refreshTimeout)
            window.removeEventListener('focus', refreshReviewState)
        }
    }, [authSessionKey, location.key])

    const reviewState =
        reviewSession.authSessionKey === authSessionKey
            ? reviewSession.state
            : authSessionKey
            ? 'checking'
            : 'approved'

    if (reviewState === 'checking') return <LoadingScreen />
    if (reviewState === 'pending_review') return <AccountReviewPlaceholder />

    return children
}

// The image auth worker was removed (it logged auth tokens). Drop any copy a browser still has.
async function unregisterImageAuthWorker() {
    try {
        if (!('serviceWorker' in navigator)) return
        const registrations = await navigator.serviceWorker.getRegistrations()
        await Promise.all(
            registrations
                .filter(r =>
                    (r.active || r.waiting || r.installing)?.scriptURL?.endsWith(
                        '/imageAuthWorker.js',
                    ),
                )
                .map(r => r.unregister()),
        )
    } catch (e) {
        // best effort
    }
}

async function registerServiceWorker() {
    if ('serviceWorker' in navigator) {
        let registration
        try {
            registration = await navigator.serviceWorker.register('/service-worker.js')
            console.log('[Companion]', 'Service worker registered!', registration)
        } catch (error) {
            console.error('[Companion]', 'Failed to register service worker:', error)
        }

        if (registration && 'PushManager' in window && window.vapidPublicKey && gon?.currentUser) {
            try {
                const { pushes_allowed } = gon?.currentUser

                if (pushes_allowed === null) {
                    const pushModal = document.querySelector('#push-modal')
                    pushModal?.classList.add('show')
                } else if (pushes_allowed) {
                    if (!('Notification' in window) || Notification.permission === 'denied') {
                        console.error(
                            'This browser does not support notifications or permission has been denied',
                        )
                        return
                    }

                    if (Notification.permission !== 'granted') {
                        const permission = await Notification.requestPermission()
                        if (permission !== 'granted') {
                            console.error('Permission to receive notifications has been denied')
                            return
                        }
                    }

                    try {
                        const pushSubscription = await registration.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: window.vapidPublicKey,
                        })

                        const subsUrl = `${window.knovApiUrl}/subscription`
                        const pushResp = await fetch(subsUrl, {
                            method: 'POST',
                            body: JSON.stringify({ push_subscription: pushSubscription }),
                            redirect: 'follow',
                            headers: { ...accessTokens(), 'Content-Type': 'application/json' },
                        })
                    } catch (error) {
                        console.error('[Companion]', 'Failed to subscribe to push manager:', error)
                    }
                }
            } catch (error) {
                console.error('[Companion]', 'Failed to subscribe to push manager:', error)
            }
        }
    }
}

function initServiceWorker() {
    if (process.env.NODE_ENV === 'production') {
        registerServiceWorker()
    }
    unregisterImageAuthWorker()
}

function getSubdomain() {
    // If the hostname is either a localhost, a numeric IP (ie a 192.168.*
    // local address), or has only two parts (indicating a primary domain like
    // treechat.ai), return null to indicate no subdomain.
    const hostnameParts = window.location.hostname.split('.')
    const isNumericIP = hostnameParts.every(part => Number.isInteger(Number(part)))
    const isLocalhost = hostnameParts[hostnameParts.length - 1] === 'localhost' || isNumericIP

    if (isLocalhost || hostnameParts.length === 2) {
        return null
    }

    return hostnameParts[0]
}

async function resolveActiveSpaceFromSubdomain() {
    const specialCaseSpaces = ['app', 'staging', 'home']
    let subdomain = getSubdomain() || 'app'

    if (specialCaseSpaces.includes(subdomain)) {
        subdomain = 'treechat'
    }

    const resolvedSpaceId = await fetch(
        `${window.knovApiUrl}/api/v1/spaces/subdomain_to_id?subdomain=${subdomain}`,
    )
        .then(response => (response.ok ? response.text() : null))
        .catch(err => {
            console.error(err)
            return null
        })

    return {
        subdomain,
        resolvedSpaceId,
    }
}

async function mountKnovApp() {
    markStartupMetric('mount-app-started')
    const seededSpaceId = getBootSpaceId()
    window.__KNOV_PENDING_ACTIVE_SPACE_ID__ = seededSpaceId
    if (seededSpaceId) {
        useStore.getState().set({ activeSpaceId: seededSpaceId })
    }

    try {
        initMobileAppEventHandlers()

        resolveActiveSpaceFromSubdomain()
            .then(({ subdomain, resolvedSpaceId }) => {
                if (!resolvedSpaceId) {
                    return
                }

                window.__KNOV_PENDING_ACTIVE_SPACE_ID__ = resolvedSpaceId
                useStore.getState().set({ activeSpaceId: resolvedSpaceId })
                console.log('>>> launching with subdomain and space id', subdomain, resolvedSpaceId)
                markStartupMetric('subdomain-space-resolved', {
                    subdomain,
                    hasSpaceId: Boolean(resolvedSpaceId),
                })
                logEv('LOAD SPACE', { subdomain, spaceId: resolvedSpaceId })
            })
            .catch(e => {
                console.log('>>> error while loading space from subdomain!', e)
            })

        const router = createBrowserRouter(
            createRoutesFromElements([
                <Route path="/" element={<LoginRedirector />} />,
                <Route path="/login" element={<Login />} />,
                <Route
                    path="/signup"
                    element={(() => {
                        const SignupLoader = () => {
                            const { search } = useLocation()
                            const authQuery = parseAuthQuery(search)
                            return (
                                <Signup
                                    space_invite_token={authQuery.space_invite}
                                    stream_invite_token={authQuery.stream_invite}
                                    stream_join={authQuery.stream_join}
                                    wallet={authQuery.wallet}
                                    oauth_signup_token={authQuery.oauth_signup}
                                    attribution={authQuery}
                                />
                            )
                        }

                        return <SignupLoader />
                    })()}
                />,
                <Route
                    path="/auth/password/request-reset"
                    element={(() => {
                        const RequestPasswordResetLoader = () => {
                            const RequestPasswordResetWithLoader =
                                withAppLoader(RequestPasswordReset)
                            return <RequestPasswordResetWithLoader load />
                        }

                        return <RequestPasswordResetLoader />
                    })()}
                />,
                <Route
                    path="/auth/password/edit"
                    element={(() => {
                        const ResetPasswordLoader = () => {
                            const { search } = useLocation()
                            const { reset_password_token } = queryString.parse(search)
                            const ResetPasswordWithLoader = withAppLoader(ResetPassword)
                            return (
                                <ResetPasswordWithLoader
                                    load
                                    resetPasswordToken={reset_password_token}
                                />
                            )
                        }

                        return <ResetPasswordLoader />
                    })()}
                />,

                <Route path="/profile" element={<UserSettingsLoader />} />,
                // Public account-deletion link (treechat.com/delete-account points here;
                // listed in the Play Console): settings, scrolled to Delete Account.
                <Route
                    path="/delete-account"
                    element={<UserSettingsLoader focusDeleteAccount />}
                />,

                <Route
                    path="/user/:userKey/ordinals"
                    element={(() => {
                        const UserOrdinalsLoader = () => {
                            let { userKey } = useParams()

                            const load = async () => {
                                const userId = idFromKey(userKey)

                                if (userId) {
                                    return [
                                        true,
                                        {
                                            filter: {
                                                user_ordinals: userId,
                                            },
                                        },
                                    ]
                                } else {
                                    const user = await api.getUserByName(userKey.toLowerCase())

                                    if (user) {
                                        return [
                                            true,
                                            {
                                                filter: {
                                                    user_ordinals: user.id,
                                                },
                                            },
                                        ]
                                    } else {
                                        return [false]
                                    }
                                }
                            }

                            return <StateProviderLoader load={load} />
                        }

                        return <UserOrdinalsLoader />
                    })()}
                />,

                <Route
                    path="/user/:userKey/music"
                    element={(() => {
                        const UserMusicLoader = () => {
                            let { userKey } = useParams()

                            const load = async () => {
                                const userId = idFromKey(userKey)

                                if (userId) {
                                    return [
                                        true,
                                        {
                                            filter: {
                                                user_music: userId,
                                            },
                                        },
                                    ]
                                } else {
                                    const user = await api.getUserByName(userKey.toLowerCase())

                                    if (user) {
                                        return [
                                            true,
                                            {
                                                filter: {
                                                    user_music: user.id,
                                                },
                                            },
                                        ]
                                    } else {
                                        return [false]
                                    }
                                }
                            }

                            return <StateProviderLoader load={load} />
                        }

                        return <UserMusicLoader />
                    })()}
                />,

                <Route
                    path="/user/:userKey/replies"
                    element={(() => {
                        const UserRepliesLoader = () => {
                            const { userKey } = useParams()

                            const load = async () => {
                                const userId = idFromKey(userKey)
                                if (userId) {
                                    return [
                                        true,
                                        {
                                            filter: {
                                                user: userId,
                                                profile_thread_scope: 'replies',
                                            },
                                        },
                                    ]
                                }

                                const user = await api.getUserByName(userKey.toLowerCase())
                                return user
                                    ? [
                                          true,
                                          {
                                              filter: {
                                                  user: user.id,
                                                  profile_thread_scope: 'replies',
                                              },
                                          },
                                      ]
                                    : [false]
                            }

                            return <StateProviderLoader load={load} />
                        }

                        return <UserRepliesLoader />
                    })()}
                />,

                <Route
                    path="/user/:userKey"
                    element={(() => {
                        // due to hooks + react-router, create a mini component just to route the data in.
                        // note that this mini component is being called as an IIFE! (note the trailing `()`)
                        const UserLoader = () => {
                            let { userKey } = useParams()

                            // Load function to handle both UUID and username cases
                            const load = async () => {
                                // First try to convert as UUID
                                const userId = idFromKey(userKey)

                                if (userId) {
                                    // If userId is valid, it's already a UUID
                                    return [
                                        true,
                                        {
                                            filter: {
                                                user: userId,
                                                profile_thread_scope: 'posts',
                                            },
                                        },
                                    ]
                                } else {
                                    // If not a UUID, try to fetch by username
                                    const user = await api.getUserByName(userKey.toLowerCase())

                                    if (user) {
                                        return [
                                            true,
                                            {
                                                filter: {
                                                    user: user.id,
                                                    profile_thread_scope: 'posts',
                                                },
                                            },
                                        ]
                                    } else {
                                        // User not found
                                        return [false]
                                    }
                                }
                            }

                            return <StateProviderLoader load={load} />
                        }

                        return <UserLoader />
                    })()}
                />,

                <Route path="/stream/:streamId/invite" element={<StreamInvite />} />,

                <Route
                    path="/stream/people"
                    element={
                        // Legacy People stream URLs land on the new Home.
                        <StateProviderLoader
                            load={true}
                            filter={{ following: true, order: 'time' }}
                        />
                    }
                />,

                <Route
                    // Old Artist links: the artist's profile, Music tab.
                    path="/music/artist/:artistId"
                    element={(() => {
                        const MusicArtistLoader = () => {
                            const { artistId } = useParams()
                            return (
                                <StateProviderLoader
                                    load={true}
                                    filter={{ user_music: artistId }}
                                />
                            )
                        }

                        return <MusicArtistLoader />
                    })()}
                />,

                <Route
                    path="/people/:participantKey"
                    element={(() => {
                        // Mini component so we can read the participant key from the route.
                        const ParticipantsLoader = () => {
                            const { participantKey } = useParams()
                            return (
                                <StateProviderLoader
                                    load={true}
                                    filter={{ participants: participantKey, order: 'time' }}
                                />
                            )
                        }

                        return <ParticipantsLoader />
                    })()}
                />,

                <Route
                    path={'/stream/public'}
                    element={(() => {
                        return (
                            <StateProviderLoader
                                load={true}
                                filter={{ public: true, order: 'time' }}
                            />
                        )
                    })()}
                />,

                <Route
                    path={'/stream/following'}
                    element={(() => {
                        return (
                            <StateProviderLoader
                                load={true}
                                filter={{ following: true, order: 'time' }}
                            />
                        )
                    })()}
                />,

                <Route
                    path={'/stream/treechat'}
                    element={(() => {
                        return (
                            <StateProviderLoader
                                load={true}
                                filter={{ treechat: true, order: 'time' }}
                            />
                        )
                    })()}
                />,

                <Route
                    path={'/stream/hodlocker'}
                    element={(() => {
                        return (
                            <StateProviderLoader
                                load={true}
                                filter={{ hodlocker: true, order: 'time' }}
                            />
                        )
                    })()}
                />,

                <Route
                    path={'/stream/twetch'}
                    element={(() => {
                        return (
                            <StateProviderLoader
                                load={true}
                                filter={{ twetch: true, order: 'time' }}
                            />
                        )
                    })()}
                />,

                <Route
                    path={'/stream/history'}
                    element={(() => {
                        return (
                            <StateProviderLoader
                                load={true}
                                filter={{ history: true, order: 'time' }}
                            />
                        )
                    })()}
                />,

                <Route
                    path={'/stream/starred'}
                    element={(() => {
                        return (
                            <StateProviderLoader
                                load={true}
                                filter={{ starred: true, order: 'time' }}
                            />
                        )
                    })()}
                />,

                <Route
                    path="/stream/all"
                    element={<StateProviderLoader filter={{ all: true, order: 'time' }} />}
                />,

                <Route
                    path="/stream/private"
                    element={
                        <StateProviderLoader
                            filter={{ private: true, private_all: true, order: 'time' }}
                        />
                    }
                />,

                <Route
                    path="/stream/private/threads"
                    element={<StateProviderLoader filter={{ private: true, order: 'time' }} />}
                />,

                <Route
                    path="/stream/private/mentions"
                    element={
                        <StateProviderLoader
                            filter={{ private: true, private_mentions: true, order: 'time' }}
                        />
                    }
                />,

                <Route
                    path="/stream/private/messages"
                    element={
                        <StateProviderLoader
                            filter={{ private: true, private_messages: true, order: 'time' }}
                        />
                    }
                />,

                <Route
                    path="/stream/private/requests"
                    element={
                        <StateProviderLoader
                            filter={{
                                private: true,
                                private_message_requests: true,
                                order: 'time',
                            }}
                        />
                    }
                />,

                <Route
                    path="/stream/link-access"
                    element={<StateProviderLoader filter={{ link: true, order: 'time' }} />}
                />,

                <Route
                    path="/stream/clips"
                    element={<StateProviderLoader filter={{ clip: true, order: 'time' }} />}
                />,

                <Route
                    path="/stream/public-clips"
                    element={<StateProviderLoader filter={{ public_clip: true, order: 'time' }} />}
                />,

                <Route
                    path="/stream/shared"
                    element={<StateProviderLoader filter={{ link: true, order: 'time' }} />}
                />,

                <Route
                    path="/stream/notifications"
                    element={<StateProviderLoader filter={{ notifications: true }} />}
                />,

                <Route
                    path="/stream/ordinals"
                    element={<StateProviderLoader filter={{ ordinals: true }} />}
                />,

                <Route
                    path="/market"
                    element={
                        <StateProviderLoader
                            load={true}
                            filter={{ market: true, market_cards: true, order: 'time' }}
                        />
                    }
                />,
                <Route
                    path="/market/items"
                    element={
                        <StateProviderLoader
                            load={true}
                            filter={{ market: true, ordinals: true, order: 'time' }}
                        />
                    }
                />,
                // Activity is the default lens; the explicit URL stays valid.
                <Route path="/market/activity" element={<Navigate to="/market" replace />} />,
                // The Ordinals > Market tab became the Market surface's Items lens.
                <Route
                    path="/stream/ordinals/market"
                    element={<Navigate to="/market/items" replace />}
                />,

                <Route
                    path="/stream/ordinals/mine"
                    element={
                        <StateProviderLoader filter={{ ordinals: true, ordinals_scope: 'mine' }} />
                    }
                />,

                <Route
                    path="/stream/ordinals/following"
                    element={<Navigate to="/stream/ordinals" replace />}
                />,

                // Legacy alias — the streams discovery feed now lives at /streams.
                <Route path="/discovery/streams" element={<Navigate to="/streams" replace />} />,

                // The station's obvious URLs (/radio is linked from treechat.com/music)
                // open the Music stream. The no-login player stays at the API host's /radio.
                <Route path="/radio" element={<RadioRedirect />} />,
                <Route path="/music" element={<RadioRedirect />} />,

                <Route
                    path="/streams/mine"
                    element={
                        <StateProviderLoader
                            load={true}
                            filter={{ streams: true, streams_scope: 'mine' }}
                        />
                    }
                />,

                <Route
                    path="/streams/following"
                    element={
                        <StateProviderLoader
                            load={true}
                            filter={{ streams: true, streams_scope: 'following' }}
                        />
                    }
                />,

                <Route
                    path="/streams/private"
                    element={
                        <StateProviderLoader
                            load={true}
                            filter={{ streams: true, streams_scope: 'private' }}
                        />
                    }
                />,

                // Legacy alias — the memes discovery feed now lives at /memes.
                <Route path="/discovery/memes" element={<Navigate to="/memes" replace />} />,

                <Route path="/modal/metrics" element={<MetricsModalLoader />} />,
                <Route path="/modal/ordinals" element={<OrdinalsModalLoader />} />,
                <Route path="/modal/ordinals/:ordinalId" element={<OrdinalsModalLoader />} />,
                <Route path="/modal/leaderboards" element={<LeaderboardModalLoader />} />,
                <Route path="/modal/leaderboards/:category" element={<LeaderboardModalLoader />} />,
                <Route path="/modal/tdp" element={<TdpModalLoader />} />,
                <Route path="/tdp/share" element={<TdpSharePage />} />,
                <Route path="/p/:shareId" element={<EarningsShareLandingPage />} />,
                <Route path="/settings" element={<SettingsModalLoader />} />,

                // Commented out - now using modal instead of dedicated route
                // <Route
                //     path="/stream/:streamId/settings"
                //     element={(() => {
                //         // due to hooks + react-router, create a mini component just to route the data in.
                //         // note that this mini component is being called as an IIFE! (note the trailing `()`)
                //         const StreamSettingsParams = () => {
                //             let { streamId } = useParams()
                //             return <StreamSettingsLoader teamId={streamId} />
                //         }

                //         return <StreamSettingsParams />
                //     })()}
                // />,

                <Route
                    path="/stream/:streamId/search"
                    element={(() => {
                        const SearchStreamLoader = () => {
                            let { streamId } = useParams()
                            const location = useLocation()
                            const { q } = queryString.parse(location.search)

                            // Use the filterFromPath function to build the filter
                            const filter = filterFromPath(location.pathname, q)

                            return <StateProviderLoader streamId={streamId} filter={filter} />
                        }

                        return <SearchStreamLoader />
                    })()}
                />,

                <Route
                    path="/stream/:streamId"
                    element={(() => {
                        // due to hooks + react-router, create a mini component just to route the data in.
                        // note that this mini component is being called as an IIFE! (note the trailing `()`)
                        const StreamLoader = () => {
                            let { streamId } = useParams()
                            return (
                                <StateProviderLoader
                                    load={true}
                                    streamId={streamId}
                                    filter={{ team_ids: [streamId] }}
                                />
                            )
                        }

                        return <StreamLoader />
                    })()}
                />,

                // The discovery feeds work logged-out (anonymous gon + public-only
                // APIs), so /memes and /streams serve the same UI to everyone.
                // On a hard load Rails serves the SSR SEO directory at these URLs
                // and the feed hydrates over it (same pattern as /quest/:slug).
                <Route
                    path="/memes"
                    element={<StateProviderLoader load={true} filter={{ memes: true }} />}
                />,

                <Route
                    path="/memes/mine"
                    element={
                        <StateProviderLoader
                            load={true}
                            filter={{ memes: true, memes_scope: 'mine' }}
                        />
                    }
                />,

                <Route
                    path="/streams"
                    element={<StateProviderLoader load={true} filter={{ streams: true }} />}
                />,

                // Legacy SEO-directory URLs; Rails 301s hard loads, this covers
                // any stale client-side link.
                <Route path="/hub/memes" element={<Navigate to="/memes" replace />} />,

                <Route path="/hub/streams" element={<Navigate to="/streams" replace />} />,

                <Route
                    path="/meme/:memeText/stream/:streamId"
                    element={(() => {
                        // due to hooks + react-router, create a mini component just to route the data in.
                        // note that this mini component is being called as an IIFE! (note the trailing `()`)
                        const MemeLoader = () => {
                            let { memeText } = useParams()
                            memeText = memeText ? decodeURIComponent(memeText) : null
                            memeText = memeText?.split('-').join(' ')
                            return (
                                <StateProviderLoader
                                    load={true}
                                    memeText={memeText}
                                    filter={{ meme: memeText }}
                                />
                            )
                        }

                        return <MemeLoader />
                    })()}
                />,

                <Route
                    path="/meme/:memeText"
                    element={(() => {
                        // due to hooks + react-router, create a mini component just to route the data in.
                        // note that this mini component is being called as an IIFE! (note the trailing `()`)
                        const MemeLoader = () => {
                            let { memeText } = useParams()
                            memeText = memeText ? decodeURIComponent(memeText) : null
                            memeText = memeText?.split('-').join(' ')
                            return (
                                <StateProviderLoader
                                    load={true}
                                    memeText={memeText}
                                    filter={{ meme: memeText, all: true }}
                                />
                            )
                        }

                        return <MemeLoader />
                    })()}
                />,

                <Route
                    path="/quest/:questKey/answer/:answerId"
                    element={(() => {
                        const QuestAnswerLoader = () => {
                            const { hash } = useLocation()
                            const { questKey, answerId } = useParams()
                            const questId = idFromKey(questKey)

                            const load = async () => {
                                // Always return true to allow navigation to the quest page
                                // The PrivateQuest component will handle showing the private message
                                return [true]
                            }

                            return (
                                <StateProviderLoader
                                    load={load}
                                    filter={{ questId, answerId }}
                                    hash={hash}
                                />
                            )
                        }

                        return <QuestAnswerLoader />
                    })()}
                />,

                <Route
                    path="/quest/:questKey"
                    element={(() => {
                        // due to hooks + react-router, create a mini component just to route the data in.
                        // note that this mini component is being called as an IIFE! (note the trailing `()`)
                        const QuestLoader = () => {
                            const { hash } = useLocation()
                            const { questKey } = useParams()
                            const questId = idFromKey(questKey)

                            const load = () => {
                                // Always return true to allow navigation to the quest page
                                // The PrivateQuest component will handle showing the private message
                                return [true]
                            }

                            return (
                                <StateProviderLoader load={load} filter={{ questId }} hash={hash} />
                            )
                        }

                        return <QuestLoader />
                    })()}
                />,

                <Route
                    path="/new"
                    element={(() => {
                        const QuestLoader = () => {
                            const load = () => {
                                const filter = { public: true }
                                const draftQuest = questModel.newQuestFromFilter({}, filter)
                                cacheQuest(draftQuest)

                                return [true, { filter: { questId: draftQuest.id } }]
                            }
                            return <StateProviderLoader load={load} />
                        }

                        return <QuestLoader />
                    })()}
                />,

                <Route
                    path="/spaces/:spaceId/settings"
                    element={(() => {
                        // due to hooks + react-router, create a mini component just to route the data in.
                        // note that this mini component is being called as an IIFE! (note the trailing `()`)
                        const SpaceSettingsParams = () => {
                            let { spaceId } = useParams()
                            return <SpaceSettingsLoader spaceId={spaceId} />
                        }

                        return <SpaceSettingsParams />
                    })()}
                />,
                <Route
                    path="/spaces/invitation"
                    element={(() => {
                        // due to hooks + react-router, create a mini component just to route the data in.
                        // note that this mini component is being called as an IIFE! (note the trailing `()`)
                        const SpaceInvitationParams = () => {
                            const { search } = useLocation()
                            const { space_invite } = queryString.parse(search)

                            const load = async () => {
                                // space_invite represents the space invitation id.
                                const invitation = await api.getSpaceInvitationByToken(space_invite)
                                if (invitation) {
                                    return [true, { invitation }]
                                } else {
                                    // TODO (?): handle this better than just redirecting to login.
                                    return [false]
                                }
                            }

                            return <SpaceInvitationLoader load={load} />
                        }

                        return <SpaceInvitationParams />
                    })()}
                />,
            ]).map(route => ({
                ...route,
                element: <AccountReviewGate>{route.element}</AccountReviewGate>,
            })),
        )

        // Keep the tab title honest across in-app navigation. Fires only on
        // navigation, so the server-rendered title on the initial render (the
        // one crawlers index) is left untouched.
        router.subscribe(state => {
            setDocumentTitleForPath(state.location.pathname)
        })

        // Create a wrapper component that will use the version check hook
        const AppWithVersionCheck = ({ children }) => {
            // Initialize version checking (runs every 30 minutes)
            useVersionCheck(30)

            // After returning from Stripe Checkout, sync subscription info from session_id.
            // This is especially important in local dev where Stripe webhooks may not be forwarded.
            useEffect(() => {
                try {
                    const params = new URLSearchParams(window.location.search)
                    const status = params.get('stripe_ai_billing')
                    const sessionId = params.get('session_id')

                    if (status !== 'success' || !sessionId) {
                        return
                    }

                    api.syncStripeAiBillingCheckoutSession(sessionId).then(res => {
                        if (res?.ok) {
                            params.delete('stripe_ai_billing')
                            params.delete('session_id')
                            const qs = params.toString()
                            const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`
                            window.location.assign(nextUrl)
                        } else if (res?.error) {
                            alert(res.error)
                        } else {
                            alert('Stripe billing sync failed')
                        }
                    })
                } catch (e) {
                    // ignore
                }
            }, [])

            // Send theme changes to native Expo app via eventBus$
            // (the subscriber at line ~1307 forwards MsgSource.Web messages to ReactNativeWebView)
            const appearanceTheme = useStore(state => state.appearanceTheme)
            const lastPersistedThemeRef = useRef(
                window.gon?.currentUser?.space_options?.theme ?? null,
            )
            // Store-backed (StateProvider hydrates from gon after login) so the effect
            // re-runs when features arrive; reading window.gon directly would race the
            // initial mount. Entries may be strings or { name } objects.
            const userFeatures = useStore(state => state.userFeatures)
            const bsvUnit = useStore(state => state.bsvUnit)
            const hasNeonAccent = !!userFeatures?.some(
                feature =>
                    (typeof feature === 'string' ? feature : feature?.name) === 'neon_accent',
            )
            const moneyColorMode = resolveMoneyColorMode(bsvUnit)
            const effectiveTheme = resolveEffectiveTheme(appearanceTheme)
            const isMobileDevice = isMobile
            const documentThemeVariant = `${
                isMobileDevice ? 'mobile' : 'desktop'
            }-${effectiveTheme}`

            useEffect(() => {
                if (typeof document !== 'undefined') {
                    document.documentElement.setAttribute('data-theme', documentThemeVariant)
                    document.documentElement.style.colorScheme = effectiveTheme

                    // neon_accent feature flag: glow accents in dark mode. Stamped as a
                    // root attribute so the flag composes with theme selectors in CSS
                    // (html[data-neon-accent='true'][data-theme$='dark']).
                    if (hasNeonAccent) {
                        document.documentElement.setAttribute('data-neon-accent', 'true')
                    } else {
                        document.documentElement.removeAttribute('data-neon-accent')
                    }
                }

                if (typeof window !== 'undefined') {
                    window.localStorage?.setItem(THEME_STORAGE_KEY, appearanceTheme)

                    const persistence = persistAppearanceThemePreference({
                        appearanceTheme,
                        currentUser: window.gon?.currentUser,
                        lastPersistedThemeRef,
                        persist: api.updateUserSpaceOptions,
                    })
                    void persistence?.catch(error =>
                        console.error('Failed to persist theme preference', error),
                    )
                }
            }, [appearanceTheme, effectiveTheme, documentThemeVariant, hasNeonAccent])

            useEffect(() => {
                if (typeof document === 'undefined') return

                document.documentElement.setAttribute('data-money-color', moneyColorMode)
            }, [moneyColorMode])

            const sendThemeToNative = useCallback(() => {
                if (!window.KNOVIGATOR_IS_MOBILE) {
                    return () => {}
                }

                let rafId = 0
                rafId = window.requestAnimationFrame(() => {
                    const statusBarColor = effectiveTheme === 'dark' ? '#04060b' : '#f9fafb'

                    const payload = JSON.stringify({
                        preference: appearanceTheme,
                        effective: effectiveTheme,
                        statusBarColor: statusBarColor,
                    })

                    const message = new Message(payload, MsgSource.Web, MsgTypes.WebAppThemeChange)
                    eventBus$.next(message)

                    console.log('>>> Sent WebAppThemeChange to native:', {
                        preference: appearanceTheme,
                        effective: effectiveTheme,
                        statusBarColor: statusBarColor,
                    })
                })

                return () => {
                    if (rafId) window.cancelAnimationFrame(rafId)
                }
            }, [appearanceTheme, effectiveTheme])

            // Send on preference change and initial load
            useEffect(() => {
                return sendThemeToNative()
            }, [sendThemeToNative])

            // Compatible native builds wait for this signal before forwarding a
            // pending share. Send it after the panel navigator can show the posted
            // thread.
            useEffect(() => {
                if (
                    !window.KNOVIGATOR_IS_MOBILE ||
                    !window.gon?.currentUser ||
                    userFeatures === null
                )
                    return

                let cancelled = false
                void waitForShareCondition(
                    isShareNavigationHostReady,
                    'Treechat panel navigation did not become ready.',
                    20000,
                )
                    .then(() => {
                        if (cancelled) return
                        eventBus$.next(
                            new Message(
                                JSON.stringify({ enabled: true }),
                                MsgSource.Web,
                                MsgTypes.ShareComposerFeatureChange,
                            ),
                        )
                    })
                    .catch(error => {
                        console.error('Unable to enable native share handoff:', error)
                    })

                return () => {
                    cancelled = true
                }
            }, [userFeatures])

            // Also send when system theme changes (for "system" mode)
            useEffect(() => {
                if (appearanceTheme !== 'system') {
                    return
                }

                const handleSystemThemeChange = () => {
                    sendThemeToNative()
                }

                window.addEventListener('nativeSystemThemeChange', handleSystemThemeChange)
                return () => {
                    window.removeEventListener('nativeSystemThemeChange', handleSystemThemeChange)
                }
            }, [appearanceTheme, sendThemeToNative])

            // Answer the native shell's back-button requests. Harmless on the
            // web, where nothing ever dispatches the event.
            useEffect(() => initNativeBackBridge({ navigate: navigateBack }), [])

            return children
        }

        const container = document.getElementById('knovigator-body')
        const root = createRoot(container)
        const app = (
            <AppWithVersionCheck>
                <RouterProvider router={router} />
            </AppWithVersionCheck>
        )
        root.render(app)
        markStartupMetric('router-rendered')
    } catch (e) {
        console.log('Error loading knov!', e)
        // TODO: we can use react now, so handle this with react
        // react isn't loaded yet, so use old school dom manipulation to add a
        // class to the spinner elem and show the err msg if something failed
        const errorElem = document.querySelector('body')

        const preElem = document.createElement('pre')
        /** @type {HTMLPreElement} */ preElem.style.cssText = 'text-align: left;'
        preElem.textContent = e.stack

        errorElem.appendChild(preElem)
    }
}

const handleMobileNotiClicked = debounce(function handleMobileNotiClick(notiEv) {
    console.log('>>> noti ev received: ', notiEv)

    let content = notiEv?.notification?.request?.content

    // sometimes noti content gets sent through stringified, so try to parse it
    // as json if we get a string
    if (typeof content === 'string') {
        try {
            content = JSON.parse(content)
        } catch (e) {
            console.log('>>> failed to parse stringified content: ', e)
        }
    }

    const { data } = content ?? {}
    const { quest_id, answer_id, path } = data ?? {}
    const pathFilter = typeof path === 'string' ? filterFromPath(path) : null
    const targetQuestId = quest_id || pathFilter?.questId
    const targetAnswerId = answer_id || pathFilter?.answerId

    if (targetQuestId) {
        openCenter({ questId: targetQuestId, answerId: targetAnswerId })
        // bandaid to ensure quest loads properly, sometimes frontend navs to quest but fails to
        // load quest data
        setTimeout(() => {
            queryClient.refetchQueries({ queryKey: ['quest', targetQuestId], exact: true })
        })
    } else {
        console.log('>>> no quest_id found in notification data')
        openNotificationsStream()
    }
}, 300)

const redirectToQuest = debounce(
    async function redirectToQuest(quest) {
        const doRedirToQuest = async () => {
            try {
                openCenter({ questId: quest.id })
            } catch (err) {
                // debugPopover(`error getting quest: ${err}`)
            }
        }

        doRedirToQuest()
    },
    1000,
    { leading: false, trailing: true },
)

const SHARE_NAVIGATION_POLL_MS = 25
const shareUrlHandoffs = new Map()

function isShareNavigationHostReady() {
    const swiper = getSwiperRef()?.current
    return Boolean(swiper && !swiper.destroyed && getCenterPanel())
}

function waitForShareCondition(predicate, timeoutMessage, timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
        const startedAt = Date.now()

        const check = () => {
            if (predicate()) {
                resolve()
                return
            }
            if (Date.now() - startedAt >= timeoutMs) {
                reject(new Error(timeoutMessage))
                return
            }
            window.setTimeout(check, SHARE_NAVIGATION_POLL_MS)
        }

        check()
    })
}

// debounced to prevent double clips
const handleTextClip = memoizeDebounce(
    async ({ data, content, destination }) => {
        if (!data) throw new Error('The shared item did not include any text.')

        const isUrl = /^https?:\/\//i.test(data)
        const clippedQuest = await api.clipLink({
            ...(isUrl ? { url: data } : { content: [data, content].filter(Boolean).join('\n') }),
            ...(destination ? { destination } : {}),
            ...(isUrl && content ? { content } : {}),
        })
        redirectToQuest(clippedQuest)
    },
    2000,
    { leading: true, trailing: false },
)

function getMimeType(fileData) {
    const fileInfo = fileTypeChecker.detectFile(fileData)

    return fileInfo?.mimeType
}

const handleFileClip = memoizeDebounce(
    ({ data: b64data, mimeType, content, ...extra }) => {
        try {
            const fileData = Uint8Array.from(atob(b64data), c => c.charCodeAt(0))
            if (!mimeType) {
                mimeType = getMimeType(fileData)
            }

            const resolvedMimeType = mimeType || 'application/octet-stream'
            /** @type {Record<string, unknown>} */
            const clipParams = { ...(content ? { content } : {}) }
            if (resolvedMimeType.startsWith('image/')) {
                clipParams['image'] = fileData
            } else if (resolvedMimeType.startsWith('video/')) {
                clipParams['video'] = fileData
            } else {
                clipParams['file'] = fileData
            }
            if (extra?.destination) {
                clipParams.destination = extra.destination
            }
            return api.clipLink(clipParams).then(clippedQuest => {
                redirectToQuest(clippedQuest)
            })
        } catch (e) {
            debugPopover(`Error handling file share: ${e}\nStack Trace: ${e.stack}`)
        }
    },
    1000,
    { leading: false, trailing: true },
)

async function handleUploadedFileClip({ signedId, name, mimeType, size, content, destination }) {
    if (!signedId) throw new Error('The mobile upload did not return a file identifier.')

    const uploadedFile = {
        signedId,
        name: name || `shared-${Date.now()}`,
        mimeType: mimeType || 'application/octet-stream',
        size: Number(size) || 0,
    }

    const clippedQuest = await api.clipLink({
        uploadedFile,
        ...(content ? { content } : {}),
        ...(destination ? { destination } : {}),
    })
    redirectToQuest(clippedQuest)
}

function initMobileAppEventHandlers() {
    if (!window.KNOVIGATOR_IS_MOBILE) return
    console.log('>>> Activating mobile event handlers')

    eventBus$
        .pipe(
            filter(msg => msg.Source !== MsgSource.Web),
            filter(msg => msg.type === MsgTypes.ShareUrl),
        )
        .subscribe(msg => {
            let handoff = shareUrlHandoffs.get(msg.id)
            if (!handoff) {
                handoff = Promise.resolve().then(() => handleTextClip(JSON.parse(msg.content)))
                shareUrlHandoffs.set(msg.id, handoff)
                window.setTimeout(() => {
                    if (shareUrlHandoffs.get(msg.id) === handoff) {
                        shareUrlHandoffs.delete(msg.id)
                    }
                }, 60000)
            }

            handoff.then(
                () => {
                    replyTo(eventBus$, msg, MsgSource.Web, JSON.stringify({ ok: true }))
                },
                error => {
                    const message = error instanceof Error ? error.message : String(error)
                    replyTo(
                        eventBus$,
                        msg,
                        MsgSource.Web,
                        JSON.stringify({ ok: false, error: message }),
                    )
                },
            )
        })

    eventBus$
        .pipe(
            filter(msg => msg.source !== MsgSource.Web),
            filter(msg => msg.type === MsgTypes.ShareUploadSupport),
        )
        .subscribe(msg => {
            replyTo(eventBus$, msg, MsgSource.Web, JSON.stringify({ multipartShareUpload: true }))
        })

    eventBus$
        .pipe(
            filter(msg => msg.source !== MsgSource.Web),
            filter(msg => msg.type === MsgTypes.ShareUploadedFile),
        )
        .subscribe(async msg => {
            try {
                await handleUploadedFileClip(JSON.parse(msg.content))
                replyTo(eventBus$, msg, MsgSource.Web, JSON.stringify({ ok: true }))
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error)
                replyTo(
                    eventBus$,
                    msg,
                    MsgSource.Web,
                    JSON.stringify({ ok: false, error: message }),
                )
            }
        })

    eventBus$
        .pipe(
            filter(msg => msg.Source !== MsgSource.Web),
            filter(msg => msg.type === MsgTypes.ShareFile),
        )
        .subscribe(msg => {
            try {
                handleFileClip(JSON.parse(msg.content))
            } catch (e) {
                // debugPopover(`grrr: ${e}`)
            }
            replyTo(eventBus$, msg, MsgSource.Web, 'ok')
        })

    eventBus$
        .pipe(
            filter(msg => msg.source !== MsgSource.Web),
            filter(msg => msg.type === MsgTypes.Notification),
        )
        .subscribe(msg => {
            handleMobileNotiClicked(JSON.parse(msg.content))
            replyTo(eventBus$, msg, MsgSource.Web, 'ok')
        })

    // forward webapp messages to the native app
    eventBus$.pipe(filter(msg => msg.source === MsgSource.Web)).subscribe(msg => {
        // alert(`>>> WEBAPP msg! ${msg.toJson()}`)
        postMessageToNative(msg.toJson(), window, error => {
            console.warn('Failed to forward message to native app:', error)
        })
    })

    // reply to ping (heartbeat) messages
    eventBus$
        .pipe(
            filter(msg => msg.source !== MsgSource.Web),
            filter(msg => msg.type === MsgTypes.Ping),
        )
        .subscribe(msg => {
            replyTo(eventBus$, msg, MsgSource.Web, `pong ${new Date().toISOString()}`)
        })

    // handle system theme changes from native app
    eventBus$
        .pipe(
            filter(msg => msg.source !== MsgSource.Web),
            filter(msg => msg.type === MsgTypes.SystemThemeChange),
        )
        .subscribe(msg => {
            const newTheme = msg.content
            if (newTheme === 'light' || newTheme === 'dark') {
                window.NATIVE_SYSTEM_THEME = newTheme
                // Dispatch a custom event so components can listen for system theme changes
                window.dispatchEvent(
                    new CustomEvent('nativeSystemThemeChange', { detail: { theme: newTheme } }),
                )
            }
        })

    // forward incoming msgs from expo to eventbus$
    document.addEventListener(
        'knovigatorMsg',
        event => {
            // alert(`received message! msg: ${JSON.stringify(event.detail)}`)
            const incomingMsg = Message.fromJson(event.detail)
            eventBus$.next(incomingMsg)
        },
        false,
    )
}

function initAmplitude(userId) {
    const win = window
    if (win?.amplitude) {
        console.log('Initializing amplitude')
        win.amplitude.getInstance().init('af9082f410808de56a1082ad60c29920', userId)
    } else {
        console.log('window.amplitude not found, not loading!')
    }
}

function sentryTraceSampleRate(samplingContext) {
    const name = String(samplingContext?.name || samplingContext?.transactionContext?.name || '')
    const attributes =
        samplingContext?.attributes || samplingContext?.transactionContext?.data || {}
    const target = String(
        attributes?.['http.target'] || attributes?.['http.url'] || attributes?.url || '',
    )
    const haystack = `${name} ${target}`

    // Upload traces are high-volume and already covered by explicit error reporting.
    if (
        haystack.includes('/api/v1/uploads/') ||
        haystack.includes('/rails/active_storage/direct_uploads')
    ) {
        return 0
    }

    // Search-as-you-type and similar request bursts do not need full trace coverage.
    if (haystack.includes('/search_actions')) {
        return 0.02
    }

    // Keep a small but useful sample of page loads and navigations.
    if (/pageload|navigation/i.test(name)) {
        return 0.05
    }

    return 0.05
}

function initSentry() {
    initSentryOnce({ init: initializeSentry, isInitialized: isSentryInitialized }, () => ({
        dsn: 'https://f0a6a944bf3a414894e3eb908cdf6fdb@o455929.ingest.sentry.io/5448411',
        integrations: [browserTracingIntegration()],
        tracesSampler: sentryTraceSampleRate,
        environment: gon?.env,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
    }))
}

function initAxios() {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = gon.formToken
    axios.defaults.headers.common['X-CLIENT-VERSION'] = gon.clientVersion
}

mountKnovApp()
